/**
 * Client-side price filtering utilities for Inventory module.
 * 
 * These functions mirror the server-side logic in:
 * - server/modules/polanerApi/common.polanerapi.js (getMostRecentPrice, getLinkedPricesByState)
 * - server/graphql/dataParsers/index.js (getParsedPrice, removeFirstValueFromPrice)
 * - server/graphql/queries/inventory/*.js (priceC, priceB, discountC, discountB, discounts)
 * 
 * This allows the client to filter prices locally when month/state dropdowns
 * change, eliminating the need for a server round-trip.
 */

import moment from 'moment';

/**
 * Filter prices by state (Level field).
 * 
 * @param {Array} prices - Array of price objects with Level field
 * @param {string} state - State code, e.g., 'Y' (NY), 'J' (NJ)
 * @returns {Array} - Prices filtered to the specified state
 */
export const getLinkedPricesByState = (prices, state) => {
  if (!prices || !Array.isArray(prices)) return [];
  return prices.filter(p => p.Level === state);
};

/**
 * Check if an item has an EFFECTIVE price for a given state and month.
 * Used for visibility filtering when state/month dropdown changes.
 * 
 * IMPORTANT: This checks for effective prices (date <= selected month),
 * not just any price for the state. Items with only future-dated prices
 * should be hidden from the grid.
 * 
 * @param {Object} item - The inventory row item with linkedPrices
 * @param {string} state - State code, e.g., 'Y' (NY), 'J' (NJ)
 * @param {string} month - Month in 'YYYY-MM' format
 * @returns {boolean} - True if item has an effective price for the state/month
 */
export const itemHasPricesForState = (item, state, month) => {
  if (!item || !item.linkedPrices || !Array.isArray(item.linkedPrices)) {
    return false;
  }
  // Check if there's an EFFECTIVE price for this state (not just any price)
  // This mirrors server-side logic where items without valid prices are excluded
  const pricesForState = getLinkedPricesByState(item.linkedPrices, state);
  if (pricesForState.length === 0) {
    return false;
  }
  const effectivePrice = getMostRecentPrice(pricesForState, month);
  return effectivePrice !== null;
};

/**
 * Find the most recent price that is effective on or before the selected month.
 * 
 * Logic mirrors server-side getMostRecentPrice:
 * 1. Filter prices with Date <= selectedMonth
 * 2. Return the most recent of those
 * 3. If none found, return null (NO FALLBACK - matches server behavior)
 * 
 * IMPORTANT: Unlike previous client implementation, this does NOT fall back
 * to linkedPrices[0] when no valid price exists. Items with only future-dated
 * prices should return null and be filtered out of the grid.
 * 
 * @param {Array} linkedPrices - Array of price objects for a specific state
 * @param {string} selectedMonth - Month in 'YYYY-MM' format
 * @returns {Object|null} - The selected price object, or null if none available
 */
export const getMostRecentPrice = (linkedPrices, selectedMonth) => {
  if (!linkedPrices || linkedPrices.length === 0) return null;
  
  const currentMonth = moment();
  const selectedMoment = moment(selectedMonth, 'YYYY-MM');
  
  // Use current month as base if selected month is in the past
  // (needed for reactivated old orders)
  const baseMonth = selectedMoment.isBefore(currentMonth, 'month')
    ? currentMonth
    : selectedMoment;
  
  // Filter prices with Date <= baseMonth
  const validPrices = linkedPrices.filter(price => {
    if (!price.Date) return false;
    return moment(price.Date, 'YYMMDD').isSameOrBefore(baseMonth, 'month');
  });
  
  if (validPrices.length > 0) {
    // Return the most recent valid price
    return validPrices.reduce((mostRecent, price) => {
      if (!mostRecent) return price;
      if (!price.Date || !mostRecent.Date) return mostRecent;
      
      const priceDate = moment(price.Date, 'YYMMDD');
      const mostRecentDate = moment(mostRecent.Date, 'YYMMDD');
      
      return priceDate.isAfter(mostRecentDate) ? price : mostRecent;
    }, null);
  }
  
  // Server-side fallback: try to find any price not after baseMonth
  // This handles edge cases but still respects date boundaries
  const orderedPrices = linkedPrices
    .filter(p => p.Date && !moment(p.Date, 'YYMMDD').isAfter(baseMonth, 'month'))
    .sort((a, b) => (b.Date || '').localeCompare(a.Date || ''));
  
  // Return the first valid price, or null if none exist
  // IMPORTANT: Do NOT fall back to linkedPrices[0] - that would return
  // future-dated prices which should not be shown
  return orderedPrices[0] || null;
};

/**
 * Select the correct price from linkedPrices based on state and month.
 * 
 * IMPORTANT: No fallback to other states. If no price exists for the
 * selected state, returns null. This matches server-side behavior where
 * items without prices for the selected state are excluded entirely.
 * 
 * @param {Array} linkedPrices - All prices for an item (across all states/dates)
 * @param {string} state - State code, e.g., 'Y' (NY), 'J' (NJ)
 * @param {string} month - Month in 'YYYY-MM' format
 * @returns {Object|null} - The selected price object, or null if none for this state
 */
export const selectPrice = (linkedPrices, state, month) => {
  if (!linkedPrices || linkedPrices.length === 0) return null;
  
  // Filter by state - NO FALLBACK to other states
  const pricesForState = getLinkedPricesByState(linkedPrices, state);
  
  // If no prices for this state, return null (item should be hidden)
  if (pricesForState.length === 0) {
    return null;
  }
  
  return getMostRecentPrice(pricesForState, month);
};

/**
 * Parse a price value from the Price string.
 * 
 * Price string format examples:
 * - "144" (simple case price)
 * - "144,128/5,120/6" (case price with volume discounts)
 * - "55/B,45/6B,42/12B" (bottle pricing with volume discounts)
 * 
 * @param {string} priceString - The raw Price field value
 * @param {string} position - 'first' for base price, 'last' for best discount price
 * @returns {Object} - { value: number, containsSlashB: boolean }
 */
export const getParsedPrice = (priceString, position = 'first') => {
  if (!priceString || typeof priceString !== 'string') {
    return { value: 0, containsSlashB: false };
  }
  
  const values = priceString.split(',');
  const index = position === 'first' ? 0 : values.length - 1;
  const targetValue = values[index] || '';
  
  // Check if price is specified per bottle (contains /B or /NB pattern)
  const regex = /\d\/(\d\d?)?B/gm;
  
  return {
    value: parseFloat(targetValue) || 0,
    containsSlashB: !!targetValue.match(regex),
  };
};

/**
 * Remove the first value from a comma-delimited price string.
 * Used to get the "Discounts" display (all tiers except base price).
 * 
 * @param {string} priceString - The raw Price field value
 * @returns {string} - Price string with first value removed
 */
export const removeFirstValueFromPrice = (priceString) => {
  if (!priceString || typeof priceString !== 'string') return '';
  
  const values = priceString.split(',');
  
  if (values.length <= 1) return '';
  
  return values.slice(1).join(',');
};

/**
 * Compute all derived price fields from a Price string.
 * 
 * This mirrors the server-side GraphQL field resolvers:
 * - PriceC: Case price (first value, multiplied by Uom if per-bottle)
 * - PriceB: Bottle price (first value, divided by Uom if per-case)
 * - DiscountC: Best discount case price (last value, multiplied by Uom if per-bottle)
 * - DiscountB: Best discount bottle price (last value, divided by Uom if per-case)
 * - Discounts: All price tiers except the first (for display)
 * 
 * @param {string} priceString - The raw Price field value
 * @param {number} uom - Unit of measure (bottles per case)
 * @returns {Object} - { PriceC, PriceB, DiscountC, DiscountB, Discounts }
 */
export const computePriceFields = (priceString, uom) => {
  const uomValue = uom || 1;
  
  const first = getParsedPrice(priceString, 'first');
  const last = getParsedPrice(priceString, 'last');
  
  return {
    // PriceC: Case price
    PriceC: first.containsSlashB ? first.value * uomValue : first.value,
    
    // PriceB: Bottle price
    PriceB: first.containsSlashB ? first.value : first.value / uomValue,
    
    // DiscountC: Best discount case price
    DiscountC: last.containsSlashB ? last.value * uomValue : last.value,
    
    // DiscountB: Best discount bottle price
    DiscountB: last.containsSlashB ? last.value : last.value / uomValue,
    
    // Discounts: All tiers except first
    Discounts: removeFirstValueFromPrice(priceString),
  };
};

/**
 * Update price fields on a single item based on selected state and month.
 * 
 * @param {Object} item - The inventory row item
 * @param {string} state - State code, e.g., 'Y' (NY), 'J' (NJ)
 * @param {string} month - Month in 'YYYY-MM' format
 * @returns {boolean} - True if item was updated, false if no matching price found
 */
export const updateItemPriceFields = (item, state, month) => {
  if (!item || !item.linkedPrices || item.linkedPrices.length === 0) {
    return false;
  }
  
  const selectedPrice = selectPrice(item.linkedPrices, state, month);
  
  if (!selectedPrice || !selectedPrice.Price) {
    return false;
  }
  
  const fields = computePriceFields(selectedPrice.Price, item.Uom);
  
  // Update item in-place
  item.PriceC = fields.PriceC;
  item.PriceB = fields.PriceB;
  item.DiscountC = fields.DiscountC;
  item.DiscountB = fields.DiscountB;
  item.Discounts = fields.Discounts;
  item.Red = selectedPrice.Red;
  item.Level = selectedPrice.Level;
  
  return true;
};

export default {
  getLinkedPricesByState,
  itemHasPricesForState,
  getMostRecentPrice,
  selectPrice,
  getParsedPrice,
  removeFirstValueFromPrice,
  computePriceFields,
  updateItemPriceFields,
};
